import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    finalize,
    Observable,
    of,
    startWith,
    Subject,
    Subscription,
    switchMap,
    takeUntil,
    tap
} from 'rxjs';
import {
    WorkflowConfig,
    WorkflowConfigField,
    WorkflowConfigFieldSelect,
    WorkflowConfigFieldType,
    WorkflowConfigSection,
    WorkflowConfigStep,
    WorkflowData
} from '@monorepo/interfaces';
import { MessageOperationType } from '../../../shared/enums/MessageOperationType.enum';
import { SnackbarService } from '@siemens/component-lib';
import { TranslateService } from '@ngx-translate/core';
import { CreateWorkflowOperations } from '../../create-workflow/create-workflow.operations';
import { WorkflowOptionSetService } from '../../../shared/services/workflow-option-set.service';

@Injectable({
    providedIn: 'root'
})
export class WorkflowOperations {
    private workflowOptionSetService = inject(WorkflowOptionSetService);
    private snackbarService = inject(SnackbarService);
    private translateService = inject(TranslateService);

    private fieldSubscriptions: Map<string, Subscription[]> = new Map();

    public setupDependentFieldSubscriptions(
        workflowFormGroup: FormGroup,
        workflowConfig: WorkflowConfig,
        filteredWorkflowConfigSteps: WorkflowConfigStep[],
        workflow: WorkflowData,
        destroy$: Subject<boolean>,
        isAdmin: boolean,
        updateStepsCallback: (updater: (steps: WorkflowConfigStep[]) => WorkflowConfigStep[]) => void
    ): void {
        this.cleanupAllFieldSubscriptions();
        const stepsFormGroup = workflowFormGroup.get('steps') as FormGroup;
        let fieldsToProcess: WorkflowConfigField[] = null;

        if (isAdmin) {
            if (!stepsFormGroup) return;
            fieldsToProcess = [];
            filteredWorkflowConfigSteps?.forEach(stepCfg => {
                stepCfg.sections?.forEach(section => (fieldsToProcess = [...fieldsToProcess, ...section.fields]));
            });
        } else {
            fieldsToProcess = this.getActiveFieldsAndFormGroup(
                workflowFormGroup,
                workflowConfig,
                filteredWorkflowConfigSteps,
                workflow.currentStepId
            );
        }

        if (!fieldsToProcess || fieldsToProcess.length === 0) return;

        const restrictToCurrentStep = !isAdmin;

        fieldsToProcess
            .filter(field => field.fieldType === WorkflowConfigFieldType.SELECT && field.dependent?.length)
            .forEach(field => {
                const control = this.findFormControlByName(stepsFormGroup, field.name) as FormControl;
                if (control) {
                    this.setupDependentFieldSubscription(
                        field as WorkflowConfigFieldSelect,
                        control,
                        stepsFormGroup,
                        workflow,
                        destroy$,
                        updateStepsCallback,
                        restrictToCurrentStep
                    );
                }
            });
    }

    /**
     * Finds a FormControl by name within the entire FormGroup
     * @param formGroup The main FormGroup
     * @param controlName Name of the FormControl to find
     * @returns The found FormControl or null
     */
    private findFormControlByName(formGroup: FormGroup, controlName: string): FormControl | null {
        return this.searchControlRecursive(formGroup, controlName);
    }

    /**
     * Recursive search for a FormControl
     * @param control The current AbstractControl
     * @param targetName Name of the control to find
     * @returns The found FormControl or null
     */
    private searchControlRecursive(control: AbstractControl, targetName: string): FormControl | null {
        if (control instanceof FormControl) {
            return null; // FormControl cannot have children
        }

        if (control instanceof FormGroup) {
            // First check if the control is directly in this group
            if (Object.keys(control.controls).includes(targetName)) {
                const foundControl = control.get(targetName);
                if (foundControl instanceof FormControl) {
                    return foundControl;
                }
            }

            // Then recursively search through all children
            for (const [_, childControl] of Object.entries(control.controls)) {
                const result = this.searchControlRecursive(childControl, targetName);
                if (result) {
                    return result;
                }
            }
        }

        return null;
    }

    private setupDependentFieldSubscription(
        field: WorkflowConfigFieldSelect,
        control: FormControl,
        formGroup: FormGroup,
        workflow: WorkflowData,
        destroy$: Subject<boolean>,
        updateStepsCallback: (updater: (steps: WorkflowConfigStep[]) => WorkflowConfigStep[]) => void,
        restrictToCurrentStep: boolean
    ): void {
        const dependentControls = field.dependent
            .map(dependentFieldName => this.findFormControlByName(formGroup, dependentFieldName))
            .filter(control => control !== null);
        if (dependentControls.length === 0) return;

        const combinedValueChanges$ = combineLatest(
            dependentControls.map(ctrl => ctrl.valueChanges.pipe(startWith(ctrl.value), distinctUntilChanged()))
        ).pipe(
            debounceTime(100),
            distinctUntilChanged((prev, curr) => {
                return JSON.stringify(prev) === JSON.stringify(curr);
            }),
            switchMap(() => {
                const dependentValues = this.collectDependentFieldValues(field.dependent, formGroup);
                return this.updateFieldOptionsObservable(
                    field,
                    control,
                    dependentValues,
                    workflow,
                    updateStepsCallback,
                    restrictToCurrentStep
                );
            }),
            takeUntil(destroy$)
        );

        const subscription = combinedValueChanges$.subscribe();
        this.fieldSubscriptions.set(field.name, [subscription]);
    }

    private updateFieldOptionsObservable(
        field: WorkflowConfigFieldSelect,
        control: FormControl,
        dependentValues: Record<string, string>,
        workflow: WorkflowData,
        updateStepsCallback: (updater: (steps: WorkflowConfigStep[]) => WorkflowConfigStep[]) => void,
        restrictToCurrentStep: boolean
    ): Observable<string[]> {
        const dependentFieldNames = Object.keys(dependentValues);
        // Always use only the first segment of the field name (split by '-') for option requests
        const normalizedDependentFieldNames = dependentFieldNames.map(name => name.split('_')[0]);
        const [filterFieldName1, filterFieldName2] = normalizedDependentFieldNames;
        const filterFieldValue1 = dependentValues[dependentFieldNames[0]]?.toString() || '';
        const filterFieldValue2 = dependentValues[dependentFieldNames[1]]?.toString() || '';
        const fieldName = field.name.split('_')[0];
        this.workflowOptionSetService.setFieldLoading(fieldName);
        return this.workflowOptionSetService
            .getWorkflowFieldOptionSet(
                workflow.createdAt,
                fieldName,
                filterFieldName1 || null,
                filterFieldValue1 || null,
                filterFieldName2 || null,
                filterFieldValue2 || null
            )
            .pipe(
                tap((options: string[]) => {
                    const targetStepId = restrictToCurrentStep ? workflow.currentStepId : null;
                    updateStepsCallback(steps =>
                        this.updateStepsWithNewOptions(steps, targetStepId, field.name, options)
                    );
                    const currentValue = control.getRawValue();
                    const currentValues = Array.isArray(currentValue)
                        ? currentValue
                        : currentValue
                          ? [currentValue]
                          : [];
                    const allCurrentValuesValid =
                        currentValues.length > 0 && currentValues.every(v => options.includes(v));
                    if (!allCurrentValuesValid) {
                        control.setValue(options.length > 0 ? options[0] : null, { emitEvent: false });
                    }
                }),
                finalize(() => this.workflowOptionSetService.clearFieldLoading(fieldName)),
                catchError(() => {
                    this.showMessage(
                        MessageOperationType.ERROR,
                        'workflow.error.failed-options-update.title',
                        'workflow.error.failed-options-update.description'
                    );
                    return of(null);
                })
            );
    }

    private collectDependentFieldValues(dependentFields: string[], formGroup: FormGroup): Record<string, string> {
        const values: Record<string, string> = {};
        dependentFields.forEach(fieldName => {
            const control = this.findFormControlByName(formGroup, fieldName) as FormControl;
            if (control) {
                values[fieldName] = control.getRawValue();
            }
        });

        return values;
    }

    private getActiveFieldsAndFormGroup(
        workflowFormGroup: FormGroup,
        workflowConfig: WorkflowConfig,
        filteredWorkflowConfigSteps: WorkflowConfigStep[],
        activeStepId: string
    ): WorkflowConfigField[] {
        if (!workflowFormGroup || !workflowConfig) {
            return null;
        }
        const stepConfig = filteredWorkflowConfigSteps.find(step => step.id === activeStepId);
        if (!stepConfig) return null;

        let fieldsToProcess: WorkflowConfigField[] = [];
        stepConfig.sections?.map(section => (fieldsToProcess = [...fieldsToProcess, ...section.fields]));

        const stepsFormGroup = workflowFormGroup.get('steps') as FormGroup;
        const stepFormGroup = stepsFormGroup?.get(`step-${stepConfig.name}`) as FormGroup;

        if (!stepFormGroup) return null;

        return fieldsToProcess;
    }

    private updateStepsWithNewOptions(
        steps: WorkflowConfigStep[],
        // If provided, restrict updates to this step id; otherwise update all matching fields
        targetStepId: string | null,
        fieldName: string,
        options: string[]
    ): WorkflowConfigStep[] {
        if (targetStepId) {
            return steps.map(step =>
                step.id === targetStepId
                    ? {
                          ...step,
                          sections: step.sections.map((section: WorkflowConfigSection) => ({
                              ...section,
                              fields: section.fields.map((field: WorkflowConfigField) =>
                                  field.name === fieldName ? { ...field, options } : field
                              )
                          }))
                      }
                    : step
            );
        }

        return steps.map(step => ({
            ...step,
            sections: step.sections.map((section: WorkflowConfigSection) => ({
                ...section,
                fields: section.fields.map((field: WorkflowConfigField) =>
                    field.name === fieldName ? { ...field, options } : field
                )
            }))
        }));
    }

    private showMessage(operationType: MessageOperationType, title: string, description: string): Observable<boolean> {
        this.snackbarService.open(
            CreateWorkflowOperations.getSnackbarMessageConfig(operationType, title, description, this.translateService)
        );
        return of(false);
    }

    public cleanupAllFieldSubscriptions(): void {
        this.fieldSubscriptions.forEach(subscriptions => {
            subscriptions.forEach(sub => sub.unsubscribe());
        });
        this.fieldSubscriptions.clear();
    }

    public destroy(): void {
        this.cleanupAllFieldSubscriptions();
    }
}
